// Min-version contract between a paired binary (Sifter, future
// whitelabels) and the backend services it talks to. The backend
// emits `X-Sifter-Min-Version` on its responses; the substrate
// reads it and refuses to proceed when the local build is below
// the floor. Today only Keep emits it (and only pair-time calls
// are intercepted); see `docs/products/sift/SIFTER_DISTRIBUTION.md`
// for the run-time check planned for v0.2.

export const MIN_VERSION_HEADER = 'x-sifter-min-version';

export class UpgradeRequiredError extends Error {
  readonly currentVersion: string;
  readonly minVersion: string;
  readonly packageName: string;

  constructor(opts: {
    currentVersion: string;
    minVersion: string;
    packageName: string;
  }) {
    super(
      `The backend requires ${opts.packageName} ${opts.minVersion} or newer. ` +
        `You're running ${opts.currentVersion}. ` +
        `Upgrade: npm install -g ${opts.packageName}@latest`,
    );
    this.name = 'UpgradeRequiredError';
    this.currentVersion = opts.currentVersion;
    this.minVersion = opts.minVersion;
    this.packageName = opts.packageName;
  }
}

/**
 * Returns -1 if a < b, 0 if a == b, 1 if a > b. Parses only the
 * MAJOR.MINOR.PATCH numeric prefix; prerelease and build metadata
 * are ignored (treated as equal to the bare version).
 */
export function compareSemver(a: string, b: string): number {
  const parse = (v: string): [number, number, number] => {
    const core = v.replace(/^v/, '').split(/[-+]/, 1)[0]!;
    const parts = core.split('.').map((p) => Number.parseInt(p, 10));
    return [parts[0] ?? 0, parts[1] ?? 0, parts[2] ?? 0];
  };
  const [aMaj, aMin, aPat] = parse(a);
  const [bMaj, bMin, bPat] = parse(b);
  if (aMaj !== bMaj) return aMaj < bMaj ? -1 : 1;
  if (aMin !== bMin) return aMin < bMin ? -1 : 1;
  if (aPat !== bPat) return aPat < bPat ? -1 : 1;
  return 0;
}

/**
 * Inspect a backend response's headers; throw UpgradeRequiredError
 * if the local build is below the declared floor. No header is a
 * no-op (older backends that don't emit it stay forward-compatible).
 */
export function checkResponseMinVersion(opts: {
  response: Pick<Response, 'headers'>;
  currentVersion: string;
  packageName: string;
}): void {
  const minVersion = opts.response.headers.get(MIN_VERSION_HEADER);
  if (!minVersion) return;
  if (compareSemver(opts.currentVersion, minVersion) >= 0) return;
  throw new UpgradeRequiredError({
    currentVersion: opts.currentVersion,
    minVersion,
    packageName: opts.packageName,
  });
}
